/**
 * EmailConnectionSettings - OAuth email connection management (KAN-60)
 *
 * Single-card layout with:
 *   - Radio: System Email (default)
 *   - Radio: Connected Account (with inline connection info/buttons)
 *   - Fallback toggle (when connected account is selected)
 *   - Save / Reset buttons (config changes are local until saved)
 *
 * Styled to match AdminDashboard patterns (.setting-card, .toggle-switch, etc.)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { oauthService } from '../../services/api/oauthService';
import { logger } from '../../utils/logger';
import type {
  EmailConnectionStatus,
  EmailSendingMethod,
  EmailSendingConfig,
  ConnectionStatus,
} from '../../types/oauth';

interface EmailConnectionSettingsProps {
  onToast?: (message: string, type: 'success' | 'error') => void;
}

const EmailConnectionSettings: React.FC<EmailConnectionSettingsProps> = ({ onToast }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<EmailConnectionStatus | null>(null);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // Local draft of config for save/reset pattern
  const [draftConfig, setDraftConfig] = useState<EmailSendingConfig | null>(null);

  // Ref to track the popup window
  const popupRef = useRef<Window | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const response = await oauthService.getStatus();
      if (response.success) {
        setStatus(response.data);
        setDraftConfig(response.data.config);
      }
    } catch (error) {
      logger.error('Failed to load email connection status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Listen for postMessage from OAuth popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'oauth-callback') return;

      // Clean up popup ref
      popupRef.current = null;
      setConnecting(false);

      if (event.data.status === 'success') {
        onToast?.(t('settings.emailConnection.connectionSuccess'), 'success');
        loadStatus();
      } else {
        const errorMsg = event.data.message || t('settings.emailConnection.connectionError');
        onToast?.(errorMsg, 'error');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [loadStatus, onToast, t]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // --- Config dirty check ---

  const hasUnsavedChanges = Boolean(
    status && draftConfig && (
      draftConfig.method !== status.config.method ||
      draftConfig.fallbackToSystem !== status.config.fallbackToSystem
    )
  );

  // --- Actions ---

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await oauthService.authoriseMicrosoft();
      if (response.success && response.data?.authorizationUrl) {
        const width = 500;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const popup = window.open(
          response.data.authorizationUrl,
          'oauth-popup',
          `width=${width},height=${height},left=${left},top=${top},popup=yes`
        );
        if (popup) {
          popupRef.current = popup;
          // If user closes popup manually, reset connecting state
          const pollClosed = setInterval(() => {
            if (popup.closed) {
              clearInterval(pollClosed);
              popupRef.current = null;
              setConnecting(false);
            }
          }, 500);
        } else {
          // Popup blocked — fall back to redirect
          window.location.href = response.data.authorizationUrl;
        }
      } else {
        onToast?.(t('settings.emailConnection.connectionError'), 'error');
        setConnecting(false);
      }
    } catch (error) {
      logger.error('Failed to initiate OAuth flow:', error);
      onToast?.(t('settings.emailConnection.connectionError'), 'error');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const response = await oauthService.disconnect();
      if (response.success) {
        onToast?.(t('settings.emailConnection.disconnectSuccess'), 'success');
        await loadStatus();
      }
    } catch (error) {
      logger.error('Failed to disconnect:', error);
      onToast?.(t('common.error'), 'error');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await oauthService.testConnection();
      if (response.success && response.data?.success) {
        onToast?.(t('settings.emailConnection.testSuccess'), 'success');
      } else {
        onToast?.(t('settings.emailConnection.testFailed'), 'error');
      }
    } catch (error) {
      logger.error('Failed to test connection:', error);
      onToast?.(t('settings.emailConnection.testFailed'), 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleMethodChange = (method: EmailSendingMethod) => {
    if (!draftConfig) return;
    setDraftConfig({ ...draftConfig, method });
  };

  const handleFallbackToggle = (value: boolean) => {
    if (!draftConfig) return;
    setDraftConfig({ ...draftConfig, fallbackToSystem: value });
  };

  const handleSave = async () => {
    if (!status || !draftConfig) return;
    setSavingConfig(true);
    try {
      const response = await oauthService.updateConfig(draftConfig);
      if (response.success) {
        setStatus({ ...status, config: { ...draftConfig } });
        onToast?.(t('settings.emailConnection.configSaved'), 'success');
      }
    } catch (error) {
      logger.error('Failed to save email config:', error);
      onToast?.(t('common.error'), 'error');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleReset = () => {
    if (!status) return;
    setDraftConfig({ ...status.config });
  };

  // --- Helpers ---

  const connStatus: ConnectionStatus = status?.connection?.status || 'disconnected';
  const isConnected = connStatus === 'connected';
  const needsReauth = connStatus === 'requires_reauth' || connStatus === 'error';

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch {
      return dateStr;
    }
  };

  // --- Render ---

  if (loading) {
    return (
      <div className="email-settings">
        <h2>{t('settings.emailConnection.title')}</h2>
        <div className="tab-loading">
          <div className="spinner" />
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-settings">
      <h2>{t('settings.emailConnection.title')}</h2>

      <div className="setting-card">
        <div className="setting-header">
          <h3>{t('settings.emailConnection.outboundMethod')}</h3>
        </div>
        <p>{t('settings.emailConnection.description')}</p>

        <div className="email-method-options">
          {/* Option 1: System Email */}
          <label className={`email-method-option ${draftConfig?.method === 'system' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="emailMethod"
              checked={draftConfig?.method === 'system'}
              onChange={() => handleMethodChange('system')}
            />
            <div className="email-method-option-content">
              <span className="email-method-option-title">{t('settings.emailConnection.systemEmail')}</span>
              <span className="email-method-option-desc">confirmaciones@fxplus.palace.cl</span>
            </div>
          </label>

          {/* Option 2: Connected Account */}
          <label className={`email-method-option ${draftConfig?.method === 'connected_account' ? 'selected' : ''} ${!isConnected && !needsReauth ? 'disabled' : ''}`}>
            <input
              type="radio"
              name="emailMethod"
              checked={draftConfig?.method === 'connected_account'}
              onChange={() => handleMethodChange('connected_account')}
              disabled={!isConnected && !needsReauth}
            />
            <div className="email-method-option-content">
              <span className="email-method-option-title">{t('settings.emailConnection.connectedAccount')}</span>
              <span className="email-method-option-desc">
                {isConnected ? status?.connection?.connectedEmail : t('settings.emailConnection.none')}
              </span>
            </div>
          </label>

          {/* Inline connection details (inside the method options area) */}
          <div className="email-connection-inline">
            {/* State: Disconnected */}
            {!isConnected && !needsReauth && (
              <div className="email-connection-buttons">
                <button
                  className="email-btn-connect"
                  onClick={handleConnect}
                  disabled={connecting}
                >
                  {connecting ? t('common.loading') : t('settings.emailConnection.connectMicrosoft')}
                </button>
                <span className="email-coming-soon">
                  {t('settings.emailConnection.connectGmail')} — {t('settings.emailConnection.comingSoon')}
                </span>
              </div>
            )}

            {/* State: Connected */}
            {isConnected && (
              <>
                <div className="email-connected-info">
                  <div className="email-connected-meta">
                    <span>{t('settings.emailConnection.connectedAt')}: {formatDate(status?.connection?.connectedAt ?? null)}</span>
                    {status?.connection?.lastUsedAt && (
                      <span>{t('settings.emailConnection.lastUsed', { date: formatDate(status.connection.lastUsedAt) })}</span>
                    )}
                  </div>
                </div>
                <div className="email-connection-buttons">
                  <button
                    className="email-btn-test"
                    onClick={handleTestConnection}
                    disabled={testing}
                  >
                    {testing ? t('common.loading') : t('settings.emailConnection.testConnection')}
                  </button>
                  <button
                    className="email-btn-disconnect"
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                  >
                    {disconnecting ? t('common.loading') : t('settings.emailConnection.disconnect')}
                  </button>
                </div>
              </>
            )}

            {/* State: Requires reauth / error */}
            {needsReauth && (
              <>
                <div className="email-error-message">
                  <p>{t('settings.emailConnection.connectionExpired')}</p>
                  {draftConfig?.fallbackToSystem && (
                    <p className="email-fallback-note">{t('settings.emailConnection.fallbackActive')}</p>
                  )}
                </div>
                <div className="email-connection-buttons">
                  <button
                    className="email-btn-connect"
                    onClick={handleConnect}
                    disabled={connecting}
                  >
                    {connecting ? t('common.loading') : t('settings.emailConnection.reconnect')}
                  </button>
                  <button
                    className="email-btn-disconnect"
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                  >
                    {disconnecting ? t('common.loading') : t('settings.emailConnection.disconnect')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Fallback toggle - only shown when connected account is selected */}
        {draftConfig?.method === 'connected_account' && (
          <div className="email-fallback-section">
            <div className="setting-header">
              <h3>{t('settings.emailConnection.fallbackToSystem')}</h3>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={draftConfig?.fallbackToSystem ?? true}
                  onChange={(e) => handleFallbackToggle(e.target.checked)}
                />
                <span className="slider"></span>
              </label>
            </div>
            <p className="email-fallback-desc">{t('settings.emailConnection.fallbackDescription')}</p>
          </div>
        )}

        {/* Save / Reset buttons */}
        {hasUnsavedChanges && (
          <div className="email-config-actions">
            <button
              className="email-btn-connect"
              onClick={handleSave}
              disabled={savingConfig}
            >
              {savingConfig ? t('common.loading') : t('common.save')}
            </button>
            <button
              className="email-btn-test"
              onClick={handleReset}
              disabled={savingConfig}
            >
              {t('common.reset')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailConnectionSettings;
